/**
 * Turns a handoff `reason` into human copy.
 *
 * The reason arriving on /handoff/error is whatever the API produced: sometimes
 * one of our own short codes (`expired`, `invalid`), more often a raw FastAPI
 * `detail` string like "Invalid or expired token" or "Cannot claim_link from
 * status hard_expired". Rendering those verbatim made routine, expected events
 * read like system failures, so each known reason maps to a plain one-liner and
 * a tone that matches how bad it actually is — amber for "this is just how
 * links work", red only for genuine surprises.
 *
 * Kept free of JSX so the classification is unit-testable on its own; the page
 * owns the icon for each kind.
 */

export type HandoffErrorKind =
  | 'held'
  | 'claimed'
  | 'released'
  | 'declined'
  | 'not_open'
  | 'stale'
  | 'scrambled'
  | 'taken_back'
  | 'not_found'
  | 'invalid_action'
  | 'generic';

export interface HandoffErrorCopy {
  kind: HandoffErrorKind;
  /** Assignable to the shell's HandoffTone. */
  tone: 'amber' | 'rose';
  title: string;
  message: string;
}

const HELD: HandoffErrorCopy = {
  kind: 'held',
  tone: 'amber',
  title: 'Handoff already assigned',
  message: 'Another agent is working this lead.',
};

// Every non-claimable status shares one headline — the agent's situation is the
// same in all of them — and the line beneath says which status in plain words
// rather than printing the raw token.
//
// They name the status and stop there: it tells us what the row is, not who
// moved it there. A handoff is offered to one agent, so naming a teammate was a
// guess, and the same status can be reached without any agent acting.
const CLAIMED: HandoffErrorCopy = {
  kind: 'claimed',
  tone: 'amber',
  title: 'Handoff is no longer available',
  message: 'It has already been claimed.',
};

const RELEASED: HandoffErrorCopy = {
  kind: 'released',
  tone: 'amber',
  title: 'Handoff is no longer available',
  message: 'It was returned to Remi.',
};

const DECLINED: HandoffErrorCopy = {
  kind: 'declined',
  tone: 'amber',
  title: 'Handoff is no longer available',
  message: 'It was declined.',
};

const TAKEN_BACK: HandoffErrorCopy = {
  kind: 'taken_back',
  tone: 'amber',
  title: 'Handoff is no longer available',
  message: 'It went unclaimed for 72 hours and returned to Remi.',
};

/** Any other non-claimable status, including ones added after this shipped. */
const NOT_OPEN: HandoffErrorCopy = {
  kind: 'not_open',
  tone: 'amber',
  title: 'Handoff is no longer available',
  message: 'It is no longer open to claim.',
};

const STALE: HandoffErrorCopy = {
  kind: 'stale',
  tone: 'amber',
  title: 'Link expired',
  message: 'This link is no longer valid. The handoff may still be available in the app.',
};

const SCRAMBLED: HandoffErrorCopy = {
  kind: 'scrambled',
  tone: 'amber',
  title: 'Link not valid',
  message: 'Open the most recent handoff message and try again.',
};

const INVALID_ACTION: HandoffErrorCopy = {
  kind: 'invalid_action',
  tone: 'amber',
  title: 'Incomplete link',
  message: 'This link is missing its action. Open the most recent handoff message and try again.',
};

const NOT_FOUND: HandoffErrorCopy = {
  kind: 'not_found',
  tone: 'amber',
  title: 'Contact not found',
  message: 'This contact may have been merged or deleted.',
};

const GENERIC: HandoffErrorCopy = {
  kind: 'generic',
  tone: 'rose',
  title: 'Unable to load handoff',
  message: 'Please try again, or contact support if the problem continues.',
};

/**
 * Short codes the frontend itself sets when redirecting to the error page. The
 * status codes come from the landing page, which refuses to render a claim
 * screen for a handoff that is no longer claimable.
 */
const SHORT_CODES: Record<string, HandoffErrorCopy> = {
  expired: STALE,
  invalid: STALE,
  on_hold: HELD,
  already_held: HELD,
  claimed: CLAIMED,
  already_claimed: CLAIMED,
  released: RELEASED,
  returned_to_remi: RELEASED,
  declined: DECLINED,
  hard_expired: TAKEN_BACK,
  completed: NOT_OPEN,
  cancelled: NOT_OPEN,
  'missing-token': SCRAMBLED,
  'invalid-action': INVALID_ACTION,
  not_found: NOT_FOUND,
};

/**
 * Raw API `detail` strings, matched in order. Two ordering hazards:
 *  - `held by another agent` must beat the /hold/ family it contains.
 *  - `hard_expired` must beat the generic /expired/ patterns, or "Remi took it
 *    back" would be reported as an expired link.
 */
const PATTERNS: Array<{ test: RegExp; copy: HandoffErrorCopy }> = [
  { test: /another agent|being held|already held|from status hold/i, copy: HELD },
  { test: /hard_expired/i, copy: TAKEN_BACK },
  // "Invalid or expired token": the short-link lookup missed, which in practice
  // is almost always the Redis TTL running out.
  { test: /invalid or expired/i, copy: STALE },
  { test: /token expired|expired token|already been used/i, copy: STALE },
  { test: /token (signature|payload|format)|action mismatch|invalid token/i, copy: SCRAMBLED },
  { test: /missing.*token/i, copy: SCRAMBLED },
  { test: /contact not found/i, copy: NOT_FOUND },
  // A 404 on the row itself: "Handoff {id} not found".
  { test: /handoff .* not found|not found/i, copy: NOT_OPEN },
  // Infrastructure, not the link: "Token lookup failed" (Redis down) and
  // "Link token verification is not configured" (missing secret). Blaming the
  // link would send the agent off to hunt for a newer text that will not help.
  { test: /lookup failed|not configured/i, copy: GENERIC },
];

/** The landing page's prefix for "this row is not claimable, here is why". */
const STATUS_PREFIX = 'status:';

/**
 * Raw API detail strings are never shown to the agent — an unrecognised reason
 * gets the generic copy, not the backend's wording. The reason stays in the URL
 * for support to read.
 */
export const resolveHandoffError = (reason: string | null | undefined): HandoffErrorCopy => {
  const trimmed = reason?.trim();
  if (!trimmed) return GENERIC;

  if (trimmed.startsWith(STATUS_PREFIX)) {
    const status = trimmed.slice(STATUS_PREFIX.length).trim();
    return SHORT_CODES[status] ?? NOT_OPEN;
  }

  if (SHORT_CODES[trimmed]) return SHORT_CODES[trimmed];
  const matched = PATTERNS.find(({ test }) => test.test(trimmed));
  return matched ? matched.copy : GENERIC;
};
